import React, { useMemo, useState } from 'react';
import {
  money,
  formatDateInput,
  computeOr131712Cure,
  computeSaleAccounting,
} from './bhphMath';
import { printRepoPackage } from './printPackage';
import {
  printCureNotice131712,
  printSaleNotice131716,
  printNewspaperChecklist,
  printSaleResults,
  printDeficiencyDemand,
  printDeficiencyWaiver,
  printVoluntarySurrender,
  printDealerRepurchase,
} from './dispositionDocs';

const STEPS = [
  { id: 'repo', label: '1. Repo order' },
  { id: 'cure', label: '2. 1317.12 cure' },
  { id: 'sale', label: '3. 1317.16 sale' },
  { id: 'results', label: '4. Sale / close' },
  { id: 'surrender', label: 'Surrender' },
];

const inputCls =
  'w-full px-2.5 py-1.5 text-sm border border-neutral-300 rounded-md bg-white';

function Field({ label, children, className = '' }) {
  return (
    <label className={`block text-sm ${className}`}>
      <span className="text-xs text-neutral-500 block mb-0.5">{label}</span>
      {children}
    </label>
  );
}

function emptyDisposition(company, account) {
  const dpd = account?.delinquency?.daysPastDue || 0;
  const pastDueGuess = dpd > 0 ? Math.max(1, Math.ceil(dpd / 14)) : 2;
  const repoExpense = company?.default_repo_expense ?? 640;
  const storagePerDay = company?.storage_per_day ?? 20;
  return {
    reason: 'Default for nonpayment under retail installment contract.',
    lastPaymentDate: '',
    lastPaymentAmount: '',
    customerInstructions:
      'Contact Seller by phone or email immediately. Do not conceal or move the Vehicle. Personal property can be arranged for pickup. Turn in keys for $100 cash.',
    repoCompanyName: company?.repo_company_name || '',
    repoCompanyPhone: company?.repo_company_phone || '',
    repoCompanyAddress: company?.repo_company_address || '',
    repoAgent: '',
    assignedDate: formatDateInput(new Date()),
    repoDate: formatDateInput(new Date()),
    noticeDate: formatDateInput(new Date()),
    pastDueCount: String(pastDueGuess),
    lateFeeCharges: '',
    declineFeeCharges: '',
    otherDelinquencyCharges: '',
    repoExpense: String(repoExpense),
    storagePerDay: String(storagePerDay),
    storageDays: '0',
    saleDate: '',
    saleTime: '10:00 AM',
    salePlace: company?.street
      ? `${company.street}, ${[company.city, company.state, company.zip].filter(Boolean).join(', ')}`
      : '',
    minPrice: '',
    newspaperName: '',
    newspaperCounty: company?.county || 'Lucas',
    publicationDate: '',
    certifiedMailSentOn: '',
    certifiedMailTracking: '',
    amountOwed: String(account?.delinquency?.remainingPrincipal ?? account?.unpaid_balance ?? ''),
    salePrice: '',
    storageCost: '',
    otherCosts: '',
    credits: '',
    dealerIsBuyer: true,
    saleBuyerName: '',
    deficiencyAmount: '',
    deficiencyNotes: '',
    waiverNotes: '',
    closePath: 'waive',
    surrenderDate: formatDateInput(new Date()),
    surrenderOdometer: '',
    keysTurnedIn: false,
    surrenderNotes: '',
    closedOn: formatDateInput(new Date()),
  };
}

export default function BhphDispositionPanel({
  company,
  account,
  saving,
  onClose,
  onPersist,
  onSavedDoc,
}) {
  const cureUsed = !!(account?.statutory_cure_used);
  const saved = account?.disposition_json || {};
  const [step, setStep] = useState('repo');
  const [form, setForm] = useState(() => ({
    ...emptyDisposition(company, account),
    ...saved,
    lastPaymentDate:
      saved.lastPaymentDate ||
      (account?.payments?.[0] ? String(account.payments[0].paid_on).slice(0, 10) : ''),
    lastPaymentAmount:
      saved.lastPaymentAmount ||
      (account?.payments?.[0] ? String(account.payments[0].amount) : ''),
  }));

  const set = (key) => (e) => {
    const v =
      e.target.type === 'checkbox'
        ? e.target.checked
        : e.target.value;
    setForm((f) => ({ ...f, [key]: v }));
  };

  const cure = useMemo(
    () =>
      computeOr131712Cure({
        account,
        pastDueCount: form.pastDueCount,
        lateFeeCharges: form.lateFeeCharges,
        declineFeeCharges: form.declineFeeCharges,
        otherDelinquencyCharges: form.otherDelinquencyCharges,
        repoExpense: form.repoExpense,
        storagePerDay: form.storagePerDay,
        storageDays: form.storageDays,
      }),
    [account, form]
  );

  const saleAcct = useMemo(() => {
    const storageCost =
      form.storageCost !== '' && form.storageCost != null
        ? form.storageCost
        : cure.storageCost;
    return computeSaleAccounting({
      amountOwed: form.amountOwed,
      salePrice: form.salePrice,
      repoExpense: form.repoExpense,
      storageCost,
      otherCosts: form.otherCosts,
      credits: form.credits,
      dealerBid: form.dealerIsBuyer,
    });
  }, [form, cure.storageCost]);

  const saveAndPrint = async (doc_type, title, html, meta = {}, accountPatch = {}) => {
    const nextForm = { ...form, ...accountPatch };
    setForm(nextForm);
    await onPersist({
      disposition_json: nextForm,
      disposition_status:
        accountPatch.disposition_status || account.disposition_status || 'in_progress',
      status: accountPatch.status,
      last_repo_at: accountPatch.last_repo_at,
      statutory_cure_used: accountPatch.statutory_cure_used,
      statutory_cure_used_at: accountPatch.statutory_cure_used_at,
    });
    await onSavedDoc(doc_type, title, html, { ...nextForm, ...meta });
  };

  const runRepoOrder = async () => {
    if (!window.confirm('Mark account repossessed and print repo order?')) return;
    const withReinstate = {
      ...form,
      reinstateAmount: String(cure.cashToCure),
      reinstateNotes: cureUsed
        ? 'Statutory cure under ORC 1317.12 already used on this debt - no second reinstate package.'
        : `ORC 1317.12 estimated cash to cure: ${money(cure.cashToCure)} (not full balance). See cure notice for itemization.`,
    };
    setForm(withReinstate);
    const html = printRepoPackage(company, account, withReinstate);
    await saveAndPrint(
      'repo_order',
      'Repossession order / customer copies',
      html,
      { cure },
      {
        ...withReinstate,
        disposition_status: 'repossessed',
        status: 'repossessed',
        last_repo_at: new Date().toISOString(),
      }
    );
  };

  const runCureNotice = async () => {
    if (cureUsed) {
      alert(
        'Statutory cure under ORC 1317.12 was already used on this account. Do not send a second statutory reinstate package. Proceed to public sale (1317.16).'
      );
      return;
    }
    const { html, cure: c } = printCureNotice131712(company, account, form);
    await saveAndPrint('orc_1317_12_cure', 'ORC 1317.12 Cure Notice', html, { cure: c }, {
      disposition_status: 'cure_notice_sent',
      reinstateAmount: String(c.cashToCure),
    });
  };

  const markCureUsed = async () => {
    if (
      !window.confirm(
        'Mark ORC 1317.12 statutory cure as USED on this debt? This blocks a second statutory reinstate.'
      )
    )
      return;
    await onPersist({
      statutory_cure_used: true,
      statutory_cure_used_at: new Date().toISOString(),
      disposition_status: 'cure_used',
      disposition_json: { ...form, statutoryCureUsed: true },
      status: 'active',
    });
  };

  const markCustomerCured = async () => {
    if (
      !window.confirm(
        'Customer cured and took the Vehicle back? Account returns to active and statutory cure will be marked used.'
      )
    )
      return;
    await onPersist({
      statutory_cure_used: true,
      statutory_cure_used_at: new Date().toISOString(),
      disposition_status: 'cured_reinstated',
      disposition_json: { ...form, curedOn: formatDateInput(new Date()) },
      status: 'active',
    });
  };

  const runSaleNotice = async () => {
    if (!form.saleDate || !form.minPrice) {
      alert('Enter sale date and minimum price first.');
      return;
    }
    const html = printSaleNotice131716(company, account, form);
    await saveAndPrint('orc_1317_16_sale', 'ORC 1317.16 Public Sale Notice', html, {}, {
      disposition_status: 'sale_noticed',
    });
  };

  const runNewspaper = async () => {
    const html = printNewspaperChecklist(company, account, form);
    await saveAndPrint('newspaper_checklist', 'Newspaper publication checklist', html);
  };

  const runSaleResults = async () => {
    const storageCost =
      form.storageCost !== '' && form.storageCost != null
        ? form.storageCost
        : String(cure.storageCost);
    const payload = {
      ...form,
      storageCost,
      deficiencyAmount: String(saleAcct.deficiency),
    };
    setForm(payload);
    const { html, accounting } = printSaleResults(company, account, payload);
    await saveAndPrint('sale_accounting', 'Public sale results / accounting', html, {
      accounting,
    }, { disposition_status: 'sold' });
    if (payload.dealerIsBuyer) {
      const dealerHtml = printDealerRepurchase(company, account, {
        ...payload,
        closePath: payload.closePath,
      });
      await onSavedDoc('dealer_repurchase', 'Dealer purchase at public sale', dealerHtml, payload);
    }
  };

  const runDeficiencyDemand = async () => {
    const payload = { ...form, deficiencyAmount: form.deficiencyAmount || String(saleAcct.deficiency) };
    const html = printDeficiencyDemand(company, account, payload);
    await saveAndPrint('deficiency_demand', 'Deficiency demand', html, {}, {
      disposition_status: 'deficiency_pursued',
      status: 'defaulted',
    });
  };

  const runWaiverClose = async () => {
    if (
      !window.confirm(
        'Waive deficiency and close this BHPH account (no chase)? Vehicle should already have a public sale record if disposed.'
      )
    )
      return;
    const payload = {
      ...form,
      deficiencyAmount: form.deficiencyAmount || String(saleAcct.deficiency),
      closedOn: formatDateInput(new Date()),
      closePath: 'waive',
    };
    const html = printDeficiencyWaiver(company, account, payload);
    await saveAndPrint('deficiency_waived', 'Deficiency waived - account closed', html, {}, {
      disposition_status: 'deficiency_waived',
      status: 'paid_off',
      disposition_json: payload,
    });
  };

  const runSurrender = async () => {
    const html = printVoluntarySurrender(company, account, form);
    await saveAndPrint('voluntary_surrender', 'Voluntary surrender', html, {}, {
      disposition_status: 'voluntary_surrender',
      status: 'repossessed',
      last_repo_at: new Date().toISOString(),
    });
  };

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-medium text-red-900">Ohio disposition / repo desk</h3>
          <p className="text-xs text-red-800/80 mt-0.5">
            ORC 1317.12 cure · 1317.16 public sale · deficiency waive or demand. Not legal advice.
          </p>
        </div>
        <button type="button" className="text-sm border rounded-lg px-2 py-1 bg-white" onClick={onClose}>
          Close
        </button>
      </div>

      {cureUsed && (
        <div className="text-sm bg-amber-100 border border-amber-300 text-amber-950 rounded-lg px-3 py-2">
          Statutory cure under ORC 1317.12 already <strong>used</strong>
          {account.statutory_cure_used_at
            ? ` (${String(account.statutory_cure_used_at).slice(0, 10)})`
            : ''}
          . Second reinstate package is blocked - use public sale path.
        </div>
      )}

      <div className="flex flex-wrap gap-1">
        {STEPS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setStep(s.id)}
            className={`px-2.5 py-1 text-xs rounded-md border ${
              step === s.id
                ? 'bg-red-800 text-white border-red-800'
                : 'bg-white text-neutral-700 border-neutral-300'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {step === 'repo' && (
        <div className="space-y-3 bg-white border rounded-lg p-3">
          <p className="text-xs text-neutral-600">
            Repo order for seller + repo company. Marks account repossessed.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Reason" className="sm:col-span-2">
              <textarea className={inputCls + ' h-16 py-2'} value={form.reason} onChange={set('reason')} />
            </Field>
            <Field label="Last payment date">
              <input type="date" className={inputCls} value={form.lastPaymentDate} onChange={set('lastPaymentDate')} />
            </Field>
            <Field label="Last payment amount">
              <input className={inputCls} value={form.lastPaymentAmount} onChange={set('lastPaymentAmount')} />
            </Field>
            <Field label="Repo company name">
              <input className={inputCls} value={form.repoCompanyName} onChange={set('repoCompanyName')} />
            </Field>
            <Field label="Repo company phone">
              <input className={inputCls} value={form.repoCompanyPhone} onChange={set('repoCompanyPhone')} />
            </Field>
            <Field label="Repo company address" className="sm:col-span-2">
              <input className={inputCls} value={form.repoCompanyAddress} onChange={set('repoCompanyAddress')} />
            </Field>
            <Field label="Repo agent">
              <input className={inputCls} value={form.repoAgent} onChange={set('repoAgent')} />
            </Field>
            <Field label="Date assigned">
              <input type="date" className={inputCls} value={form.assignedDate} onChange={set('assignedDate')} />
            </Field>
            <Field label="Customer instructions" className="sm:col-span-2">
              <textarea
                className={inputCls + ' h-16 py-2'}
                value={form.customerInstructions}
                onChange={set('customerInstructions')}
              />
            </Field>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={runRepoOrder}
            className="px-3 py-1.5 text-sm bg-red-700 text-white rounded-lg"
          >
            Print repo order + mark repossessed
          </button>
        </div>
      )}

      {step === 'cure' && (
        <div className="space-y-3 bg-white border rounded-lg p-3">
          <p className="text-xs text-neutral-600">
            Itemized ORC 1317.12 cure. Door expense capped at $25; remainder to time balance. Cash to cure
            auto-calcs (not full note).
          </p>
          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Repo / retaking date">
              <input type="date" className={inputCls} value={form.repoDate} onChange={set('repoDate')} />
            </Field>
            <Field label="Notice date">
              <input type="date" className={inputCls} value={form.noticeDate} onChange={set('noticeDate')} />
            </Field>
            <Field label="# past-due installments (A)">
              <input className={inputCls} value={form.pastDueCount} onChange={set('pastDueCount')} />
            </Field>
            <Field label="Late fees owed (B)">
              <input className={inputCls} value={form.lateFeeCharges} onChange={set('lateFeeCharges')} />
            </Field>
            <Field label="Decline fees owed (B)">
              <input className={inputCls} value={form.declineFeeCharges} onChange={set('declineFeeCharges')} />
            </Field>
            <Field label="Other delinquency (B)">
              <input
                className={inputCls}
                value={form.otherDelinquencyCharges}
                onChange={set('otherDelinquencyCharges')}
              />
            </Field>
            <Field label="Repo expense (C)">
              <input className={inputCls} value={form.repoExpense} onChange={set('repoExpense')} />
            </Field>
            <Field label="Storage $/day">
              <input className={inputCls} value={form.storagePerDay} onChange={set('storagePerDay')} />
            </Field>
            <Field label="Storage days">
              <input className={inputCls} value={form.storageDays} onChange={set('storageDays')} />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-sm bg-neutral-50 border rounded-lg p-3">
            <div>
              <div className="text-xs text-neutral-500">(A) Past dues</div>
              <div className="font-semibold">{money(cure.pastDueAmount)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">(B) Delinquency charges</div>
              <div className="font-semibold">{money(cure.delinquencyCharges)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">(C) At door / to balance</div>
              <div className="font-semibold">
                {money(cure.expenseAtDoor)} / {money(cure.expenseToBalance)}
              </div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">(D) 2-pay deposit</div>
              <div className="font-semibold">{money(cure.twoPayDeposit)}</div>
            </div>
            <div className="sm:col-span-2 lg:col-span-4 border-t pt-2">
              <div className="text-xs text-neutral-500">Total cash / bond to cure</div>
              <div className="text-lg font-bold text-emerald-800">{money(cure.cashToCure)}</div>
              <div className="text-xs text-neutral-500">
                Expenses total {money(cure.totalExpenses)} (repo {money(cure.repoExpense)} + storage{' '}
                {money(cure.storageCost)})
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={saving || cureUsed}
              onClick={runCureNotice}
              className="px-3 py-1.5 text-sm bg-red-700 text-white rounded-lg disabled:opacity-40"
            >
              Print 1317.12 cure notice
            </button>
            <button
              type="button"
              disabled={saving || cureUsed}
              onClick={markCustomerCured}
              className="px-3 py-1.5 text-sm border border-emerald-700 text-emerald-900 rounded-lg disabled:opacity-40"
            >
              Customer cured - reinstate + mark cure used
            </button>
            <button
              type="button"
              disabled={saving || cureUsed}
              onClick={markCureUsed}
              className="px-3 py-1.5 text-sm border rounded-lg disabled:opacity-40"
            >
              Mark cure used only
            </button>
          </div>
        </div>
      )}

      {step === 'sale' && (
        <div className="space-y-3 bg-white border rounded-lg p-3">
          <p className="text-xs text-neutral-600">
            ORC 1317.16 public sale notice (certified mail + newspaper). Required when disposing of the
            Vehicle.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Sale date">
              <input type="date" className={inputCls} value={form.saleDate} onChange={set('saleDate')} />
            </Field>
            <Field label="Sale time">
              <input className={inputCls} value={form.saleTime} onChange={set('saleTime')} />
            </Field>
            <Field label="Sale place" className="sm:col-span-2">
              <input className={inputCls} value={form.salePlace} onChange={set('salePlace')} />
            </Field>
            <Field label="Minimum price">
              <input className={inputCls} value={form.minPrice} onChange={set('minPrice')} />
            </Field>
            <Field label="Certified mail sent on">
              <input
                type="date"
                className={inputCls}
                value={form.certifiedMailSentOn}
                onChange={set('certifiedMailSentOn')}
              />
            </Field>
            <Field label="Certified mail tracking #">
              <input
                className={inputCls}
                value={form.certifiedMailTracking}
                onChange={set('certifiedMailTracking')}
              />
            </Field>
            <Field label="Newspaper name">
              <input className={inputCls} value={form.newspaperName} onChange={set('newspaperName')} />
            </Field>
            <Field label="Publication county">
              <input className={inputCls} value={form.newspaperCounty} onChange={set('newspaperCounty')} />
            </Field>
            <Field label="Publication date (≥10 days before sale)">
              <input
                type="date"
                className={inputCls}
                value={form.publicationDate}
                onChange={set('publicationDate')}
              />
            </Field>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={runSaleNotice}
              className="px-3 py-1.5 text-sm bg-red-700 text-white rounded-lg"
            >
              Print 1317.16 sale notice
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={runNewspaper}
              className="px-3 py-1.5 text-sm border rounded-lg"
            >
              Print newspaper checklist
            </button>
          </div>
        </div>
      )}

      {step === 'results' && (
        <div className="space-y-3 bg-white border rounded-lg p-3">
          <p className="text-xs text-neutral-600">
            Record sale results. If the dealer buys at the public sale (credit bid), print dealer
            repurchase / inventory return. Then pursue deficiency or waive and close.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Amount owed pre-sale">
              <input className={inputCls} value={form.amountOwed} onChange={set('amountOwed')} />
            </Field>
            <Field label="Sale / bid price">
              <input className={inputCls} value={form.salePrice} onChange={set('salePrice')} />
            </Field>
            <Field label="Storage cost (total)">
              <input
                className={inputCls}
                value={form.storageCost}
                onChange={set('storageCost')}
                placeholder={String(cure.storageCost)}
              />
            </Field>
            <Field label="Other costs">
              <input className={inputCls} value={form.otherCosts} onChange={set('otherCosts')} />
            </Field>
            <Field label="Credits (2-pay deposit, etc.)">
              <input className={inputCls} value={form.credits} onChange={set('credits')} />
            </Field>
            <Field label="Sale buyer name (if not dealer)">
              <input className={inputCls} value={form.saleBuyerName} onChange={set('saleBuyerName')} />
            </Field>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input type="checkbox" checked={!!form.dealerIsBuyer} onChange={set('dealerIsBuyer')} />
              Dealer / Seller was successful bidder (inventory return)
            </label>
          </div>
          <div className="text-sm bg-neutral-50 border rounded-lg p-3 grid sm:grid-cols-3 gap-2">
            <div>
              <div className="text-xs text-neutral-500">Deficiency</div>
              <div className="font-semibold text-red-800">{money(saleAcct.deficiency)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">Surplus</div>
              <div className="font-semibold">{money(saleAcct.surplus)}</div>
            </div>
            <div>
              <div className="text-xs text-neutral-500">Sale proceeds</div>
              <div className="font-semibold">{money(saleAcct.proceeds)}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={runSaleResults}
              className="px-3 py-1.5 text-sm bg-red-700 text-white rounded-lg"
            >
              Print sale accounting{form.dealerIsBuyer ? ' + dealer repurchase' : ''}
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={runWaiverClose}
              className="px-3 py-1.5 text-sm border border-emerald-700 text-emerald-900 rounded-lg"
            >
              Waive deficiency + close account
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={runDeficiencyDemand}
              className="px-3 py-1.5 text-sm border border-red-700 text-red-900 rounded-lg"
            >
              Print deficiency demand
            </button>
          </div>
        </div>
      )}

      {step === 'surrender' && (
        <div className="space-y-3 bg-white border rounded-lg p-3">
          <p className="text-xs text-neutral-600">
            Voluntary surrender addendum (different fact pattern). Still usually needs clean sale /
            close-out afterward.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Surrender date">
              <input type="date" className={inputCls} value={form.surrenderDate} onChange={set('surrenderDate')} />
            </Field>
            <Field label="Odometer">
              <input className={inputCls} value={form.surrenderOdometer} onChange={set('surrenderOdometer')} />
            </Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!form.keysTurnedIn} onChange={set('keysTurnedIn')} />
              Keys turned in
            </label>
            <Field label="Notes" className="sm:col-span-2">
              <textarea
                className={inputCls + ' h-16 py-2'}
                value={form.surrenderNotes}
                onChange={set('surrenderNotes')}
              />
            </Field>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={runSurrender}
            className="px-3 py-1.5 text-sm bg-red-700 text-white rounded-lg"
          >
            Print voluntary surrender
          </button>
        </div>
      )}

      <p className="text-[11px] text-neutral-500">
        Forms are templates for counsel review. BHPH only - no LotSense bank ledger entries.
      </p>
    </div>
  );
}
